/**
 * W10 — the "library" vocabulary: stars, pins, share links, search filters, collection
 * permissions, templates, exports and attachments.
 *
 * Every shape here was derived from the owner's controllers (`StarsController`,
 * `PinsController`, `SharesController`, `SearchController`, `CollectionsController`,
 * `DocumentsController`, `FileOperationsController`, `AttachmentsController`) and their
 * presenters in `ApiSupport/ApiPresenters.cs`. See
 * `documentation/ai-map/features/slate-feature-transfer/waves/W10-complement.md` §1 for the
 * request/response tables these mirror.
 */
import { z } from "zod";
import { DocumentSchema } from "~/api/types";

// ── §2 Stars ────────────────────────────────────────────────────────────────────────────────

/**
 * A star is **per-user** and keyed by its OWN id, not by what it points at: unstarring needs
 * the star row's id, which is why `stars.list` is the only way to learn whether something is
 * starred. Exactly one of `documentId` / `collectionId` is meaningful.
 */
export const StarSchema = z.object({
  id: z.string(),
  documentId: z.string().nullable().default(null),
  collectionId: z.string().nullable().default(null),
  /** Fractional index for manual ordering. The server's own list is ordered by `updatedAt`. */
  index: z.string().nullable().default(null),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Star = z.infer<typeof StarSchema>;

/**
 * `stars.list` side-loads the starred DOCUMENTS so a sidebar can render titles without N reads.
 * It side-loads no collections — those are resolved from the collection list the shell already
 * holds.
 */
export const StarListSchema = z.object({
  stars: z.array(StarSchema),
  documents: z.array(DocumentSchema),
});
export type StarList = z.infer<typeof StarListSchema>;

/** What a star may point at. The server 400s when both are absent. */
export interface StarTarget {
  documentId?: string;
  collectionId?: string;
}

// ── §3 Pins ─────────────────────────────────────────────────────────────────────────────────

/**
 * A pin is **per-workspace** (`pin.TeamId`), not per-user — the whole difference from a star.
 * `collectionId` null means the pin belongs to the home surface, not to any collection.
 */
export const PinSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  collectionId: z.string().nullable().default(null),
  index: z.string().nullable().default(null),
});
export type Pin = z.infer<typeof PinSchema>;

/** `pins.list` side-loads the pinned documents, exactly as `stars.list` does. */
export const PinListSchema = z.object({
  pins: z.array(PinSchema),
  documents: z.array(DocumentSchema),
});
export type PinList = z.infer<typeof PinListSchema>;

// ── §4 Share links ──────────────────────────────────────────────────────────────────────────

/**
 * One share link, as `ApiPresenters.PresentShare` builds it.
 *
 * Deliberately a second type beside `types.ts`'s `Share`: that one is the SETTINGS row (title,
 * views, who made it) and has been stable since the shell was built; this one carries the four
 * switches the share dialog actually writes — `published`, `includeChildDocuments`,
 * `allowIndexing`, `domain` — which the list row never needed. `lastAccessedAt` is stripped by
 * the server for a non-admin, so it is optional here rather than nullable-and-always-present.
 */
export const ShareLinkSchema = z.object({
  id: z.string(),
  documentId: z.string(),
  documentTitle: z.string().nullable().default(null),
  /** The absolute link the server built (`ApiHelpers.BuildShareUrl`), honouring `domain`. */
  url: z.string(),
  urlId: z.string(),
  published: z.boolean().default(false),
  includeChildDocuments: z.boolean().default(false),
  allowIndexing: z.boolean().default(true),
  domain: z.string().nullable().default(null),
  views: z.number().default(0),
  lastAccessedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ShareLink = z.infer<typeof ShareLinkSchema>;

/** What `shares.create` / `shares.update` accept. Every field is optional but `documentId`/`id`. */
export interface ShareLinkPatch {
  published?: boolean;
  includeChildDocuments?: boolean;
  allowIndexing?: boolean;
}

// ── §5 Export ───────────────────────────────────────────────────────────────────────────────

/**
 * The formats the platform's `ParseFileOperationFormat` really recognises. Anything else falls
 * through its `switch` to markdown, so a client that offered "PDF" would hand back a `.md` and
 * call it a PDF — which is why PDF is **not** here. `knowit-markdown` is a zip of markdown
 * files (the legacy alias `outline-markdown` maps to the same thing and is not offered).
 */
export const EXPORT_FORMATS = ["markdown", "html", "json"] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

/**
 * ⚠ On the wire the states are named for the UPLOAD, not the job: `creating` is Pending and
 * `uploading` is Processing (`ApiPresenters.FormatFileOperationState`). Renamed here to what
 * they mean, once, so no surface has to explain that "uploading" means "still working".
 */
export type FileOperationState = "pending" | "running" | "complete" | "failed";

const WIRE_STATE: Record<string, FileOperationState> = {
  creating: "pending",
  uploading: "running",
  complete: "complete",
  error: "failed",
};

export const FileOperationSchema = z
  .object({
    id: z.string(),
    type: z.string().default("export"),
    format: z.string().default("markdown"),
    name: z.string().default(""),
    state: z.string().default("creating"),
    error: z.string().nullable().default(null),
    size: z.number().nullable().default(null),
    collectionId: z.string().nullable().default(null),
    createdAt: z.string().default(""),
    updatedAt: z.string().default(""),
  })
  .transform((raw) => ({
    ...raw,
    state: WIRE_STATE[raw.state] ?? ("pending" as FileOperationState),
  }));
export type FileOperation = z.infer<typeof FileOperationSchema>;

// ── §6 Search filters ───────────────────────────────────────────────────────────────────────

/** `day|week|month|year`; anything else is silently ignored by the server, so nothing else. */
export const SEARCH_DATE_FILTERS = ["day", "week", "month", "year"] as const;
export type SearchDateFilter = (typeof SEARCH_DATE_FILTERS)[number];

/** OR-ed server-side. `draft` additionally requires the viewer to be author or collaborator. */
export const SEARCH_STATUSES = ["published", "draft", "archived"] as const;
export type SearchStatus = (typeof SEARCH_STATUSES)[number];

/**
 * ⚠ `userId` filters by **collaborator** (`CollaboratorIds.Contains`), which is the nearest
 * thing the schema has to an author. Any surface that calls it "Author" is lying, so slate
 * calls it Collaborator.
 *
 * ⚠ A blank `query` short-circuits the whole endpoint to an empty result **before** any filter
 * is applied — filtering without a term is not a question this API can be asked.
 */
export interface SearchParams {
  query: string;
  collectionId?: string;
  userId?: string;
  dateFilter?: SearchDateFilter;
  statusFilter?: SearchStatus[];
  offset?: number;
  limit?: number;
}

/** One hit: the server's own snippet plus its rank, around the document. */
export const SearchHitSchema = z.object({
  id: z.string(),
  ranking: z.number().default(0),
  /** `BuildSnippet` — the matched words in context. Empty when the body had none. */
  context: z.string().default(""),
  document: DocumentSchema,
});
export type SearchHit = z.infer<typeof SearchHitSchema>;

export interface SearchResults {
  total: number;
  hits: SearchHit[];
}

/**
 * A row in `searches.list`. These are written as a SIDE EFFECT of searching (every request with
 * `offset === 0` inserts one) — never explicitly saved, which is why the UI calls them recent
 * searches and offers only "forget", not "save".
 */
export const RecentSearchSchema = z.object({
  id: z.string(),
  query: z.string(),
  source: z.string().default("app"),
  createdAt: z.string(),
  answer: z.string().nullable().default(null),
  score: z.number().nullable().default(null),
});
export type RecentSearch = z.infer<typeof RecentSearchSchema>;

// ── §8 Collection permissions ───────────────────────────────────────────────────────────────

/**
 * ⚠ The wire is ASYMMETRIC. `ParseCollectionPermission` accepts `read` / `read_write` / `admin`
 * and falls back to ReadWrite for anything else; the presenters emit
 * `Permission.ToString().ToLowerInvariant()` — `read` / **`readwrite`** / `admin`. So a value
 * read back is not the value that was sent. `normalisePermission` is the one place that knows.
 */
export const COLLECTION_PERMISSIONS = ["read", "read_write", "admin"] as const;
export type CollectionPermission = (typeof COLLECTION_PERMISSIONS)[number];

export function normalisePermission(value: unknown): CollectionPermission {
  const text = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (text === "read") {
    return "read";
  }
  if (text === "admin") {
    return "admin";
  }
  // `readwrite` (presenter) and `read_write` (parser) are the same level; so is anything the
  // server would itself fall through to ReadWrite.
  return "read_write";
}

/** A person's grant on a collection (`ApiPresenters.PresentMembership`). */
export const CollectionMembershipSchema = z
  .object({
    id: z.string(),
    userId: z.string(),
    collectionId: z.string().nullable().default(null),
    createdById: z.string().nullable().default(null),
    permission: z.unknown(),
    index: z.string().nullable().default(null),
  })
  .transform((raw) => ({ ...raw, permission: normalisePermission(raw.permission) }));
export type CollectionMembership = z.infer<typeof CollectionMembershipSchema>;

/** A group's grant (`PresentGroupMembership`). Note: no `userId`, and `groupId` instead. */
export const CollectionGroupMembershipSchema = z
  .object({
    id: z.string(),
    groupId: z.string(),
    collectionId: z.string().nullable().default(null),
    permission: z.unknown(),
  })
  .transform((raw) => ({ ...raw, permission: normalisePermission(raw.permission) }));
export type CollectionGroupMembership = z.infer<typeof CollectionGroupMembershipSchema>;

// ── §10 Attachments ─────────────────────────────────────────────────────────────────────────

/**
 * ⚠ Two steps, and the URL comes from the FIRST one: `attachments.create` reserves the row and
 * answers `{ uploadUrl, form: { attachmentId }, attachment }`; `attachments.upload` is multipart
 * and answers **204 with no body**. A client that waited for the upload to return a URL would
 * wait forever.
 */
export const AttachmentSchema = z.object({
  id: z.string(),
  name: z.string().default(""),
  contentType: z.string().default("application/octet-stream"),
  size: z.number().default(0),
  documentId: z.string().nullable().default(null),
  /** `/api/attachments.redirect?id=…` — a 302 to a signed storage URL. */
  url: z.string(),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

/** 50 MB (`[RequestSizeLimit(52428800)]`), refused by the server rather than truncated. */
export const ATTACHMENT_MAX_BYTES = 52_428_800;
